import { useState, useRef, useEffect } from "react";
import { Button } from '@prosolve/common-components-ui'
import { useReactMediaRecorder } from "react-media-recorder";
import WaveformVisualizer from "./WaveformVisualizer";
import ConfirmReRecordModal from "./ConfirmReRecordModal";

export default ({
  initialRecording,
  autoStartRecording,
  onRecordingChanged,
  onRecordingStarted,
  buttonDisplayMap = {},
  buttonBackgroundColor = '',
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [micStatus, setMicStatus] = useState("idle");
  const [timer, setTimer] = useState(0);
  const [stream, setStream] = useState(null);
  const [isRerecordConfirmationUp, setIsRerecordConfirmationUp] = useState(false)

  useEffect(() => {
    const handler = (e) => { 
      stopRecording();
      setIsRecording(false);
      stopTimer();
    };
    window.addEventListener("timesUp", handler);
    return () => window.removeEventListener("timesUp", handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const timerRef = useRef(null);

  const { startRecording, stopRecording, mediaBlobUrl } =
    useReactMediaRecorder({ audio: true, renderStream: true });

  const [currentRecording, setCurrentRecording] = useState(initialRecording)
  
  useEffect(() => {
   onRecordingChanged(mediaBlobUrl)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRecording])

  useEffect(() => {
    // Only ever set the currentRecording if this blog has a value...prevents case where we initialize with a recording that immediately gets cleared by this code
    if (mediaBlobUrl) {
      setCurrentRecording(mediaBlobUrl)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaBlobUrl])

  const startTimer = () => {
    setTimer(0);
    timerRef.current = setInterval(() => setTimer((prev) => prev + 1), 1000);
  };

  const stopTimer = () => clearInterval(timerRef.current);

  const isRequestingMic = useRef(false)

  const handleRecordClick = async () => {
    if (isRequestingMic.current) {
      return
    }

    if (micStatus === "idle") {
      try {
        isRequestingMic.current = true;
        setMicStatus("preparing");
        const s = await navigator.mediaDevices.getUserMedia({ audio: true });
        setStream(s);
        setMicStatus("ready");

        // Auto-start recording after stream is ready if we have flag set and dont already have a saved recording
        if (autoStartRecording && !currentRecording) {
          setTimeout(() => {
            if (mediaBlobUrl) stopRecording();
            startRecording();
            setIsRecording(true);
            startTimer();
            if (onRecordingStarted) {
              onRecordingStarted()
            }
          }, 0);
        }

      } catch (err) {
        console.error("Microphone access denied:", err);
        setMicStatus("idle");
      } finally {
        setTimeout(() => {
          isRequestingMic.current = false;
        }, 1)
      }
      return
    }

    if (micStatus === "ready") {
      if (!isRecording) {

        if (currentRecording) {

          if (isRerecordConfirmationUp) {
            setIsRerecordConfirmationUp(false)
          } else {
            setIsRerecordConfirmationUp(true)
            return
          }
        }

        if (mediaBlobUrl) stopRecording();
        startRecording();
        setIsRecording(true);
        startTimer();
        if (onRecordingStarted) {
          onRecordingStarted()
        }
      } else {
        stopRecording();
        setIsRecording(false);
        stopTimer();
      }
    }
  };

  useEffect(() => {
    handleRecordClick()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const formatTime = (sec) => {
    const minutes = Math.floor(sec / 60).toString().padStart(2, "0");
    const seconds = (sec % 60).toString().padStart(2, "0");
    return `${minutes}:${seconds}`;
  };

  const idleButtonDisplay = buttonDisplayMap['idle'] || 'Mic Not Enabled';
  const readyButtonDisplay = buttonDisplayMap['ready'] || 'Record';
  const preparingButtonDisplay = buttonDisplayMap['preparing'] || 'Preparing';
  const recordingButtonDisplay = buttonDisplayMap['recording'] || 'Stop';
  const readyAgainButtonDisplay = buttonDisplayMap['readyAgain'] || 'Re-record';

  return (
    <div style={{ 
      display: 'flex',
      columnGap: '80px',
      alignItems: 'center',
      justifyContent: 'center',
     }}>
      {
        isRerecordConfirmationUp &&
        <ConfirmReRecordModal onClose={() => setIsRerecordConfirmationUp(false)} onConfirmation={handleRecordClick} />
      }
      <div style={{ display: 'flex', flexDirection: 'column', width: '222px', textAlign: 'center', gap: '10px' }}>
        <Button
          style={{
            width: '200px',
            height: '200px',
            borderRadius: isRecording ? '10%' : '50%',
            padding: '10px',
            backgroundColor: isRecording ? '#fb2929' : ( buttonBackgroundColor || '#f28a22' ), // '#d2275a',
            color: '#ffffff',
            fontWeight: 'bold',
            fontSize: '24px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            flexShrink: 0
          }}
          $colored={true}
          onClick={handleRecordClick}
        >
          {isRecording &&
            <div
              style={{
                position: 'absolute',
                top: '70px',
                display: "inline-block",
                width: "12px",
                height: "12px",
                borderRadius: "50%",
                backgroundColor: "#fff",
                animation: "flash 3s infinite",
              }}
            />
          }
          <div>{isRecording ? recordingButtonDisplay : currentRecording ? readyAgainButtonDisplay : micStatus === "idle" ? idleButtonDisplay : micStatus === "preparing" ? preparingButtonDisplay : readyButtonDisplay}</div>
        </Button>
        <div style={{ display: 'flex', flexDirection: 'column', whiteSpace: 'pre-line' }}>
          {
            !isRecording ?
              (
                micStatus === "idle"
                  ?
                  <div>{'Please enable your microphone for recording'}</div>
                  :
                  micStatus === "preparing"
                    ?
                    <div>{'Preparing your microphone for recording...'}</div>
                    :
                    micStatus === "ready"
                      ?
                      // <div>{`Microphone ready.\nClick "${currentRecording ? readyAgainButtonDisplay : readyButtonDisplay}" and solve the challenge in a new window while keeping this window open.`}</div>
                      <div>{currentRecording ? `Complete.\nClick the button on the right to continue.` : `Microphone ready.\nClick "${currentRecording ? readyAgainButtonDisplay : readyButtonDisplay}" to begin.`}</div>
                      :
                      <></>
              )
              :
              <></>
          }
          {isRecording && <div>Recording... {formatTime(timer)}</div>}
        </div>
      </div>

      {
        (isRecording || currentRecording) &&
        <div style={{ display: 'flex', flexDirection: 'column' }}>

          {/* Waveform */}
          <WaveformVisualizer stream={stream} isRecording={isRecording} timer={timer} />

          {currentRecording && !isRecording && (
            <div style={{ marginTop: "1rem" }}>
              <audio src={currentRecording} controls />
            </div>
          )}

        </div>
      }

      <style>
        {`
          @keyframes flash {
            0%,50%,100%{opacity:1;}
            25%,75%{opacity:0;}
          }
        `}
      </style>
    </div>
  );
};
